/**
 * ServiceBookingFlow Component
 *
 * Wrapper component for service booking meeting types.
 * Adds service and provider selection steps before the standard booking flow.
 *
 * Layout Flow:
 * 1. Initial: Single-column centered layout (title, description, service options)
 * 2. After service selection: Animated transition to two-column layout
 *    - Left: Title, description, service summary
 *    - Right: Provider selection
 *
 * Flow: Service Selection → Provider Selection → Calendar → Time → Form
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MeetingType } from '../../../../services/meetingTypeService';
import { useServiceBookingFlow } from '../../../../hooks/useServiceBookingFlow';
import ServiceSelection from './ServiceSelection';
import ProviderSelection from './ProviderSelection';
import ServiceSummaryPanel from './ServiceSummaryPanel';
import SocialMediaLinks from '../../../../components/SocialMediaLinks';
import BrandingSash from '../../../../components/BrandingSash';
import { PublicProduct, PublicProvider } from '../../../../types/product';
import ServiceEntitlementGuard from '../../../../components/customer/ServiceEntitlementGuard';
import { clearBookingResume, readBookingResume } from '../../../../utils/bookingResume';
import { effectiveAccessRequirement } from '../../../../utils/meetingTypeAccess';
import { useCustomerAuth } from '../../../../contexts/CustomerAuthContext';

interface ServiceBookingFlowProps {
  meetingType: MeetingType;
  username: string;
  slug: string;
  /** Meeting type ID - used for preview mode when username/slug are not available */
  meetingTypeId?: number;
  onServiceProviderSelected: (
    product: PublicProduct | null,
    provider: PublicProvider | null,
    isNoPreference: boolean,
    effectiveDuration: number,
    effectivePrice: number
  ) => void;
  /** Initial step to show when component mounts (for returning from calendar) */
  initialStep?: 'SERVICE' | 'PROVIDER';
  /** Previously selected service ID (for returning from calendar) */
  previousServiceId?: number;
  /** Previously selected provider ID (for returning from calendar) - null means "No Preference" */
  previousProviderId?: number | null;
}

type ServiceStep = 'SERVICE' | 'PROVIDER';

const ServiceBookingFlow: React.FC<ServiceBookingFlowProps> = ({
  meetingType,
  username,
  slug,
  meetingTypeId,
  onServiceProviderSelected,
  initialStep = 'SERVICE',
  previousServiceId,
  previousProviderId,
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<ServiceStep>(initialStep);
  /**
   * Set by the entitlement guard: null while it is still resolving or the page
   * is open to anyone, otherwise whether THIS selection can go ahead.
   *
   * Selection is never blocked — someone picks a service, and only then are
   * they told they need points. Blocking the click would leave them staring at
   * a list they cannot touch, with no idea what any of it costs.
   */
  const [entitlementBlocked, setEntitlementBlocked] = useState(false);
  /**
   * The option `entitlementBlocked` is an answer ABOUT.
   *
   * Resolving is a round trip, so at the moment someone taps an option the
   * flag still describes the previous one. Without this the first points
   * option tapped sailed through to host selection on a stale "not blocked",
   * and the pop-up then opened over a screen they should never have reached.
   */
  const [blockedForProductId, setBlockedForProductId] = useState<number | null>(null);
  /**
   * An option chosen but not yet advanced past, because it needs points and we
   * are waiting to hear whether they have any.
   */
  const [awaitingEntitlement, setAwaitingEntitlement] = useState<number | null>(null);
  /** Bumped when a booking is actually attempted while blocked. */
  const [entitlementPrompt, setEntitlementPrompt] = useState(0);
  /**
   * The selection we have already prompted about.
   *
   * The effect below re-runs on any dependency change, and the selection
   * survives going back — so without this it raised the prompt again every
   * time, which is why dismissing the dialog and pressing Back brought it
   * straight back. One prompt per actual choice.
   */
  const promptedForRef = useRef<string | null>(null);

  /**
   * Whether a plan or points ACTUALLY cover this booking.
   *
   * Reported by the guard from the server's own resolution, not inferred from
   * the page mentioning plans. Being signed in was the old test, which hid
   * prices from someone signed in WITHOUT a plan — the one person who most
   * needed to see what they were about to be charged.
   */
  const [coveredByPlan, setCoveredByPlan] = useState(false);
  /**
   * What is LEFT on the plan this person already holds.
   *
   * Someone on the plan is not choosing whether to subscribe — they are asking
   * whether they still have a booking. So the option shows this instead of a
   * monthly price and an allowance they already bought.
   */
  const [planUsage, setPlanUsage] = useState<{ used: number; total: number | null } | null>(
    null
  );

  /**
   * The plan this viewer already holds with this host, from their session.
   *
   * Known without a request, so an option that IS their plan never advertises
   * a price at them — not even for the moment the entitlement read is in
   * flight, and not at all if that read fails.
   */
  const { walletFor } = useCustomerAuth();
  const currentPlan =
    walletFor((meetingType as any).organizationId)?.subscription ?? null;
  const currentPlanProductId = currentPlan?.productId ?? null;
  /**
   * What they pay for that plan today. A dearer plan is then quoted as the
   * DIFFERENCE — moving up credits what they already pay, so the sticker
   * price is not what upgrading costs them.
   */
  const currentPlanPrice = currentPlan?.amount ?? null;
  // Track visibility for fade transition - starts visible, fades out during transition, fades back in
  // When returning from calendar (initialStep === 'PROVIDER'), start visible since parent handles the fade-in
  const [isVisible, setIsVisible] = useState(true);
  // Track if this is a "return" mount to skip unnecessary animations
  const isReturningFromCalendar = initialStep === 'PROVIDER';

  // Use the service booking flow hook
  // Pass previousServiceId as initialServiceId when returning from calendar
  // This initializes the hook with the correct service selected and skips loading state flicker
  // Pass meetingTypeId for preview mode when username/slug are not available
  const {
    serviceOptions,
    providers,
    selectedService,
    selectedProvider,
    selectedProviderId,
    loadingServices,
    loadingProviders,
    servicesError,
    providersError,
    selectService,
    selectProvider,
    selectDuration,
    clearSelection,
    effectiveDuration,
    effectivePrice,
    isNoPreference,
    hasProviderSelection,
    skipProviderStep,
    hasNoServiceOptions,
    servicesLoaded,
    availableDurations,
    selectedDuration,
  } = useServiceBookingFlow({
    username,
    slug,
    isServiceType: meetingType.type === 'service',
    initialServiceId: previousServiceId,
    meetingTypeId: meetingTypeId,
  });

  // Check if this is duration mode (serviceMode === 'duration')
  const isDurationMode = meetingType.serviceMode === 'duration';

  // Track if we've already triggered calendar transition to prevent double triggers
  const hasTriggeredCalendarRef = useRef(false);

  // Reset trigger ref when returning from calendar so user can select provider again
  useEffect(() => {
    if (initialStep === 'PROVIDER') {
      hasTriggeredCalendarRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - intentionally omitting dependencies to avoid re-running

  /**
   * Pick up where they left off after a trip to the shop.
   *
   * Someone sent away to buy points must not come back to a blank option list —
   * that is the same choice made twice for a purchase we asked them to make.
   *
   * ⚠️ IT RESTORES THE CHOICE AND NOTHING ELSE. It used to advance to host
   * selection as well, which dropped the buyer a screen PAST the decision they
   * had just spent money on: the options they came back to look at were behind
   * them, and the balance they had bought was showing on a page that says
   * nothing about points. Restoring the selection is the whole job — moving is
   * theirs to do, and tapping the highlighted option advances as it always did.
   *
   * Safe to leave them on this step: the auto-proceed effect below needs a
   * PROVIDER before it fires, and the points prompt only fires while blocked,
   * which they no longer are.
   *
   * Consumed on read, so it restores ONCE. Left in place, a deliberate step
   * back to the option list got pulled forward again on the next render.
   */
  useEffect(() => {
    if (!meetingType?.id || !servicesLoaded) return;
    const resume = readBookingResume();
    if (!resume || resume.meetingTypeId !== meetingType.id || !resume.serviceId) return;
    // Checked BEFORE it is cleared. The options can be empty here — the fetch
    // failed, and `servicesLoaded` is set either way — and consuming first
    // would throw the note away on the one render that could not act on it.
    if (!serviceOptions.some((option) => option.id === resume.serviceId)) return;
    clearBookingResume();
    selectService(resume.serviceId);
  }, [meetingType?.id, servicesLoaded, serviceOptions, selectService]);

  // Auto-skip to PROVIDER step when there are no service options
  useEffect(() => {
    if (hasNoServiceOptions && currentStep === 'SERVICE') {
      // No service options - skip directly to provider selection
      setCurrentStep('PROVIDER');
    }
  }, [hasNoServiceOptions, currentStep]);

  // Auto-proceed to calendar when provider is selected
  useEffect(() => {
    // When there are no service options, proceed when provider is selected (no need for selectedService)
    // When there are service options, require both service and provider selection
    const canProceed = hasNoServiceOptions
      ? hasProviderSelection && !hasTriggeredCalendarRef.current
      : hasProviderSelection && selectedService && !hasTriggeredCalendarRef.current;

    // Held back until they have what this booking needs. Telling the guard NOW
    // is what makes the pop-up appear at the moment someone is stopped, rather
    // than when they merely picked a service.
    if (canProceed && entitlementBlocked) {
      // Only while they are moving FORWARD. On the service step they have
      // stepped back to reconsider, and interrupting that with the same
      // dialog they just dismissed is the bug this replaced.
      const key = `${selectedService?.id ?? 'none'}:${selectedProvider?.userId ?? 'any'}`;
      if (currentStep === 'PROVIDER' && promptedForRef.current !== key) {
        promptedForRef.current = key;
        setEntitlementPrompt((n) => n + 1);
      }
      return;
    }

    // Cleared once they are through, so a later block prompts again.
    promptedForRef.current = null;

    if (canProceed) {
      hasTriggeredCalendarRef.current = true;
      // Fade out current content first
      setIsVisible(false);
      // After fade out, call parent to show calendar
      setTimeout(() => {
        onServiceProviderSelected(
          selectedService, // Will be null when hasNoServiceOptions is true
          selectedProvider,
          isNoPreference,
          effectiveDuration,
          effectivePrice
        );
      }, 200);
    }
  }, [hasProviderSelection, hasNoServiceOptions, selectedService, selectedProvider, isNoPreference, effectiveDuration, effectivePrice, entitlementBlocked, currentStep, onServiceProviderSelected]);

  /**
   * Prompt as soon as an option is chosen that nothing on this page can settle.
   *
   * ONLY POINTS. Points are prepaid, so there is genuinely no way forward
   * without signing in or buying some, and finding that out two screens later
   * wastes every choice made in between.
   *
   * A PLAN IS NOT IN THIS SET, deliberately. Picking the plan option is picking
   * to be on the plan, and someone who is not on it yet subscribes at the end,
   * after they have seen who is available and when. Stopping them here put a
   * sign-in dialog in front of a visitor who had done nothing that needed one —
   * and left them unable to reach the calendar at all.
   */
  const selectionNeedsEntitlement = selectedService?.priceUnit === 'points';

  useEffect(() => {
    if (!selectedService || !selectionNeedsEntitlement || !entitlementBlocked) return;
    const key = `pick:${selectedService.id}`;
    if (promptedForRef.current === key) return;
    promptedForRef.current = key;
    setEntitlementPrompt((n) => n + 1);
  }, [selectedService, selectionNeedsEntitlement, entitlementBlocked]);

  /**
   * ⚠️ THESE MUST BE STABLE.
   *
   * The guard's `resolve` is a `useCallback` that lists them as dependencies,
   * and it runs in an effect keyed on that callback. Passing inline arrows
   * gave them a new identity on every render, so resolve was rebuilt every
   * render, the effect re-fired, its setState re-rendered — an endless loop of
   * entitlement requests whose state never settled. The visible symptom was a
   * plan holder still being shown a price and never a remaining count.
   *
   * Every setter below is a stable `useState` setter, so an empty dependency
   * list is correct rather than merely convenient.
   */
  const handleBlockedChange = useCallback(
    (blocked: boolean, forProductId: number | null) => {
      setEntitlementBlocked(blocked);
      setBlockedForProductId(forProductId);
    },
    []
  );

  const handleCoveredChange = useCallback(
    (covered: boolean, usage?: { used: number; total: number | null }) => {
      setCoveredByPlan(covered);
      setPlanUsage(usage ?? null);
    },
    []
  );

  /** Move on to choosing a person, with the usual fade. */
  const advanceToProvider = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      setCurrentStep('PROVIDER');
      setIsVisible(true);
    }, 200);
  }, []);

  /**
   * Is this option one we must know the answer about before moving on?
   *
   * Only points. A money price can always be paid and a plan is settled at the
   * end, but points are prepaid — someone who has none is not going anywhere,
   * and finding that out on the next screen means backing up.
   */
  const pageIsPointsPriced =
    (meetingType as any)?.paymentSettings?.enabled === true &&
    (meetingType as any)?.paymentSettings?.priceUnit === 'points';

  /**
   * The lock this page really has.
   *
   * `syncAccessRequirement` only looks at ATTACHED PRODUCTS, so a meeting type
   * priced in points at its own level keeps the column at 'none' —
   * `EntitlementService.resolve` promotes it server-side instead. The client
   * has to make the same promotion or it disagrees with the server about
   * whether the page is locked at all, and the guard that answers "may they?"
   * is never mounted to answer it.
   */
  const pageAccessRequirement = effectiveAccessRequirement(meetingType);
  const optionNeedsPoints = useCallback(
    (option?: PublicProduct | null) => option?.priceUnit === 'points' || pageIsPointsPriced,
    [pageIsPointsPriced]
  );

  // Handle service selection - triggers unified fade transition
  const handleServiceSelect = useCallback((serviceId: number) => {
    selectService(serviceId);
    // With the person-in-charge step turned off there is nothing to show next,
    // so stay put and let the auto-proceed effect move to the calendar. Moving
    // to PROVIDER first would flash a screen with one option on it.
    if (skipProviderStep) return;

    /**
     * A POINTS OPTION WAITS HERE.
     *
     * Whether they can afford it is a round trip away, and moving now would
     * carry someone with no points onto host selection only to stop them there
     * with a dialog. Staying put costs a moment and keeps the pop-up on the
     * screen the decision belongs to. The effect below moves them the instant
     * the answer comes back clear.
     */
    if (optionNeedsPoints(serviceOptions.find((option) => option.id === serviceId))) {
      setAwaitingEntitlement(serviceId);
      return;
    }

    advanceToProvider();
  }, [selectService, skipProviderStep, optionNeedsPoints, serviceOptions, advanceToProvider]);

  /**
   * Release the held option once the guard has answered about THAT option.
   *
   * Blocked, they stay exactly where they are and the prompt effect raises the
   * dialog. Clear, they carry on as if nothing had paused.
   */
  useEffect(() => {
    if (awaitingEntitlement === null) return;

    /**
     * Never wait forever.
     *
     * Waiting is only safe while something is guaranteed to answer. If the
     * guard is not mounted, or its request never returns, an option tap would
     * simply do nothing and the page would look broken with no way forward.
     * After a moment, let them through — the server re-checks on submit and is
     * the authority anyway, so the worst case is being stopped one screen
     * later instead of this one.
     */
    const failOpen = setTimeout(() => {
      setAwaitingEntitlement(null);
      advanceToProvider();
    }, 4000);

    // Still the previous option's answer — wait for this one's.
    if (blockedForProductId !== awaitingEntitlement) return () => clearTimeout(failOpen);

    clearTimeout(failOpen);
    setAwaitingEntitlement(null);
    if (entitlementBlocked) return;
    advanceToProvider();
    return () => clearTimeout(failOpen);
  }, [awaitingEntitlement, blockedForProductId, entitlementBlocked, advanceToProvider]);

  // Handle provider selection
  const handleProviderSelect = useCallback((providerId: number | null) => {
    selectProvider(providerId);
  }, [selectProvider]);

  // Handle back to service selection - unified fade transition
  const handleBackToServices = useCallback(() => {
    // Fade out current content
    setIsVisible(false);
    // After fade out, change step and fade back in
    setTimeout(() => {
      setCurrentStep('SERVICE');
      setIsVisible(true);
    }, 200);
  }, []);


  return (
    <div className="bg-white lg:rounded-lg lg:shadow-sm relative">
      {/* Branding Sash */}
      {meetingType?.user && (
        <BrandingSash hideBranding={meetingType.user.hideSailLabBranding || false} />
      )}

      {/* Content container with unified fade transition */}
      <div
        className={`transition-opacity duration-200 ease-in-out ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/*
          ONE GUARD, ABOVE BOTH STEPS.

          It used to live inside the service step, which meant a DURATION-MODE
          page never showed it at all: that step is auto-skipped when there are
          no service options, so a booking priced in points had no sign-in
          prompt, no balance, no way to buy any — and failed at submit with a
          bare error. Sitting here it also survives the move to provider
          selection, where someone short on points is stopped.

          Rendering it twice was the alternative and is worse: two resolutions
          of the same question, and two pop-ups racing to answer it.
        */}
        {pageAccessRequirement !== 'none' && (
          <ServiceEntitlementGuard
            meetingTypeId={meetingType.id}
            organizationId={(meetingType as any).organizationId}
            username={username}
            accessRequirement={pageAccessRequirement}
            selectedProductId={selectedService?.id ?? null}
            /**
             * The duration being booked. A points-priced page charges per
             * duration exactly as a money one does, so without this the cost
             * resolved to zero and a customer with a full wallet was told they
             * were short.
             */
            durationMinutes={effectiveDuration}
            onBlockedChange={handleBlockedChange}
            onCoveredChange={handleCoveredChange}
            /**
             * Whether the CHOSEN option is one this step must stop for.
             *
             * Points only. There is no money price to fall back on and nothing
             * to buy without an identified wallet, so a booking priced in
             * points cannot start until that is settled.
             *
             * An ordinary service does not qualify — no plan simply means the
             * usual price. Nor does a PLAN option any more: that is bought at
             * the end, on the checkout the server asks for once it sees the
             * plan is not theirs yet.
             */
            requiresEntitlement={
              selectedService?.priceUnit === 'points' ||
              ((meetingType as any)?.paymentSettings?.enabled === true &&
                (meetingType as any)?.paymentSettings?.priceUnit === 'points')
            }
            selectedProductKind={selectedService?.kind}
            promptToken={entitlementPrompt}
            /**
             * Back to the options once they have signed in.
             *
             * What they hold has just changed, and the option list is the only
             * screen that shows it — the balance banner, and any price that is
             * now covered. Leaving them on provider selection means the first
             * thing they see after signing in says nothing about what just
             * happened.
             *
             * The forward trigger is reset with it, or the auto-proceed effect
             * would consider the calendar already reached and refuse to move
             * when they choose again.
             */
            onIdentityChanged={() => {
              if (hasNoServiceOptions) return;
              hasTriggeredCalendarRef.current = false;
              promptedForRef.current = null;
              /**
               * DROP THE SELECTION, or the option they were stopped on cannot
               * be chosen again.
               *
               * Advancing is driven by a CHANGE of selection, so an option that
               * is still selected swallows the next tap: they sign in, land back
               * on the list, press the very option they just signed in for, and
               * nothing happens. Clearing it makes that tap a real choice again
               * — and re-resolves the entitlement against the identity they now
               * have, rather than the answer that stopped them.
               */
              clearSelection();
              // The answer that stopped them was about an identity they no
              // longer have. Dropped with the selection, so nothing left over
              // can advance them or block them on it.
              setAwaitingEntitlement(null);
              setBlockedForProductId(null);
              setCurrentStep('SERVICE');
            }}
            /**
             * DISMISSING THE POP-UP PUTS THE OPTION BACK ON THE SHELF.
             *
             * The dialog is raised by a CHOICE, and closing it without doing
             * anything about what it asked for leaves that choice standing.
             * Advancing is driven by a CHANGE of selection, so the option they
             * were stopped on then swallows the next tap on itself: they close
             * the dialog, press it again, and the page does nothing — on the one
             * control they are trying to use. Releasing the selection makes that
             * tap a real choice again, and re-resolves the entitlement instead of
             * leaving the answer that stopped them lying around.
             *
             * Nothing to release on a duration-mode page — there is no option
             * list, and the wall there is about the page itself.
             */
            onDismissed={() => {
              if (hasNoServiceOptions) return;
              clearSelection();
              // The prompt is raised once per choice; without this reset the
              // same option would be a repeat of a choice already prompted for
              // and could never raise the dialog again.
              promptedForRef.current = null;
              hasTriggeredCalendarRef.current = false;
              setAwaitingEntitlement(null);
              setBlockedForProductId(null);
              // Nothing is selected, so nothing is blocked. The re-resolve that
              // follows says the same thing; setting it here just avoids a
              // dialog flashing back over the list on the way.
              setEntitlementBlocked(false);
              setCurrentStep('SERVICE');
            }}
            /**
             * The sign-in prompt belongs to the option step only — where they
             * are still deciding. On a duration-mode page there is no option
             * step, so provider selection IS the first screen and it belongs
             * there instead.
             */
            showSignInBanner={currentStep === 'SERVICE' || hasNoServiceOptions}
            /* Supplied here because only the step knows its own column width.
               The guard applies it to its banners and renders nothing at all
               when it has none, so a page with nothing to say has no gap. */
            bannerClassName={
              currentStep === 'SERVICE'
                ? 'max-w-2xl mx-auto px-4 pt-4 lg:px-6 lg:pt-6'
                : 'px-4 pt-4 lg:px-6 lg:pt-6'
            }
          />
        )}

        {currentStep === 'SERVICE' ? (
          /* ===== SINGLE COLUMN LAYOUT (Service Selection) ===== */
          /* NOTE: Social media links are NOT shown here. Per design, they only appear */
          /* after the left column shows (PROVIDER step and beyond) for service bookings. */
          <div className="max-w-2xl mx-auto p-4 lg:p-6">
            <div className="space-y-6">
              {/* Meeting Title & Description */}
              <div className="text-center">
                <h3 className="text-2xl font-bold text-secondary-900">
                  {meetingType.title || t('booking:serviceBooking.defaultTitle', 'Service Booking')}
                </h3>
                {meetingType.description && (
                  <p className="text-sm text-secondary-600 mt-2">
                    {meetingType.description}
                  </p>
                )}
              </div>

              {/* Service Selection - Below title/description */}
              <div className="pt-4">
                <ServiceSelection
                  services={serviceOptions}
                  selectedServiceId={selectedService?.id || null}
                  onServiceSelect={handleServiceSelect}
                  loading={loadingServices}
                  error={servicesError}
                  currency={meetingType.pricing?.currency || 'USD'}
                  // When this meeting type is paid for with points, every
                  // service shows a point cost instead of money.
                  pointsMode={['point', 'point_or_subscription'].includes(
                    (meetingType as any).accessRequirement ?? 'none'
                  )}
                  defaultPointCost={(meetingType as any).pointCost ?? null}
                  hidePrice={coveredByPlan}
                  planUsage={planUsage}
                  /**
                   * The plan this viewer already holds, straight from their
                   * session.
                   *
                   * A SECOND, INDEPENDENT source of truth beside the
                   * entitlement read. That read is a round trip and can be
                   * slow, stale or failed, and while it is any of those the
                   * option advertises a monthly price to the person already
                   * paying it. Whether this plan is mine is a fact the session
                   * already knows, with no request at all.
                   */
                  currentPlanProductId={currentPlanProductId}
                  currentPlanPrice={currentPlanPrice}
                />
              </div>
            </div>
          </div>
        ) : (
          /* ===== TWO COLUMN LAYOUT (Provider Selection) ===== */
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 p-4 lg:p-6">
            {/* Left Column - Meeting Details & Service Summary */}
            <div className="lg:col-span-4">
              <div className="h-full px-4 lg:px-0 lg:border-r lg:border-secondary-200 lg:pr-6">
                <ServiceSummaryPanel
                  meetingType={meetingType}
                  selectedService={selectedService}
                  selectedProvider={selectedProvider}
                  isNoPreference={isNoPreference}
                  effectiveDuration={effectiveDuration}
                  effectivePrice={effectivePrice}
                  showSocialLinks={true}
                  // For duration mode: show duration and location in left column
                  showDurationLocation={isDurationMode}
                  availableDurations={availableDurations}
                  selectedDuration={selectedDuration}
                  onDurationChange={selectDuration}
                />

                {/* Social Media Links - Bottom Position (for PROVIDER step, appears at bottom of left column) */}
                {meetingType.hostDisplaySettings?.socialMediaDisplayPosition === 'bottom' && (
                  <SocialMediaLinks
                    twitterUrl={meetingType.twitterUrl}
                    facebookUrl={meetingType.facebookUrl}
                    instagramUrl={meetingType.instagramUrl}
                    linkedinUrl={meetingType.linkedinUrl}
                    youtubeUrl={meetingType.youtubeUrl}
                    lineUrl={meetingType.lineUrl}
                    websiteUrl={meetingType.websiteUrl}
                    socialMediaOrder={meetingType.socialMediaOrder}
                    className="mt-4"
                  />
                )}
              </div>
            </div>

            {/* Right Column - Provider Selection */}
            <div className="lg:col-span-8">
              <div className="h-full px-4 lg:px-0 space-y-6">
                <ProviderSelection
                  providers={providers}
                  selectedProviderId={selectedProviderId}
                  onProviderSelect={handleProviderSelect}
                  selectedService={selectedService}
                  loading={loadingProviders}
                  error={providersError}
                  onBack={hasNoServiceOptions ? undefined : handleBackToServices}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceBookingFlow;
